require("../categories-content/categories-content");
require("../portfolio-content/portfolio-content");
require("../about-content/about-content");

/* eslint-disable global-require */
const STORAGE_KEY = "nm_articles_content";
const DEFAULT_COVER = require("../info-preview/img/info-preview-03.jpg");
const DEFAULT_AUTHOR_IMAGE = require("../employee-preview/img/employee-01.jpg");
/* eslint-enable global-require */

const CYRILLIC_MAP = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z",
  и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
  с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const BLOCK_TYPES = ["heading", "section", "text", "image", "list", "cta", "slider"];

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildTitleHtml(title) {
  const words = String(title || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return `<span>${escapeHtml(words[0])}</span>`;
  return `<span>${escapeHtml(words[0])}</span> ${escapeHtml(words.slice(1).join(" "))}`;
}

function slugify(value) {
  const slug = String(value || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((char) => {
      if (CYRILLIC_MAP[char] !== undefined) return CYRILLIC_MAP[char];
      if (/[a-z0-9]/.test(char)) return char;
      if (char === " " || char === "_" || char === "-") return "-";
      return "";
    })
    .join("")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "article";
}

function uniqueSlug(base, excludeId, articles) {
  const source = Array.isArray(articles) ? articles : [];
  const root = base || "article";
  const used = source.filter((item) => item.id !== excludeId).map((item) => item.slug);
  if (!used.includes(root)) return root;
  let index = 2;
  let candidate = `${root}-${index}`;
  while (used.includes(candidate)) {
    index += 1;
    candidate = `${root}-${index}`;
  }
  return candidate;
}

function getArticlePath(article) {
  return `/article/${encodeURIComponent(article.slug || article.id)}`;
}

function formatDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function todayIso() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function safeHref(value) {
  if (window.NMSanitize?.safeHref) return window.NMSanitize.safeHref(value);
  const href = String(value || "").trim();
  if (!href || /^\s*javascript:/i.test(href)) return "";
  if (href.startsWith("//")) return "";
  if (/^(https?:\/\/|\/|#|mailto:|tel:)/i.test(href)) return href;
  return "";
}

function renderInlineMarkdown(text) {
  const source = String(text || "");
  let result = "";
  let cursor = 0;
  const pattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match = pattern.exec(source);
  while (match) {
    result += escapeHtml(source.slice(cursor, match.index));
    const label = escapeHtml(match[1]);
    const href = safeHref(match[2]);
    result += href ? `<a href="${escapeHtml(href)}">${label}</a>` : label;
    cursor = match.index + match[0].length;
    match = pattern.exec(source);
  }
  result += escapeHtml(source.slice(cursor));
  return result.replace(/\n/g, "<br>");
}

function normalizeHeadingBlock(block = {}) {
  const level = Math.min(5, Math.max(1, Number(block.level) || 2));
  const text = String(block.text || "").trim();
  if (!text) return null;
  return {
    id: String(block.id || createId()),
    type: "heading",
    level,
    text,
    accent: String(block.accent || "").trim(),
  };
}

function normalizeSectionBlock(block = {}) {
  const text = String(block.text || "").trim();
  if (!text) return null;
  return {
    id: String(block.id || createId()),
    type: "section",
    text,
    accent: String(block.accent || "").trim(),
    anchor: String(block.anchor || "").trim() || `section-${slugify(text)}`,
  };
}

function normalizeTextBlock(block = {}) {
  const text = String(block.text || "").trim();
  if (!text) return null;
  const variant = ["normal", "large", "bold"].includes(block.variant) ? block.variant : "normal";
  return {
    id: String(block.id || createId()),
    type: "text",
    text,
    variant,
  };
}

function normalizeImageBlock(block = {}) {
  const src = String(block.src || "").trim();
  if (!src) return null;
  return {
    id: String(block.id || createId()),
    type: "image",
    src,
    alt: String(block.alt || "").trim(),
  };
}

function normalizeListBlock(block = {}) {
  const items = (Array.isArray(block.items) ? block.items : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  if (!items.length) return null;
  return {
    id: String(block.id || createId()),
    type: "list",
    ordered: Boolean(block.ordered),
    items,
  };
}

function normalizeSliderBlock(block = {}) {
  const slides = (Array.isArray(block.slides) ? block.slides : [])
    .map((slide) => ({
      image: String(slide?.image || "").trim(),
      caption: String(slide?.caption || "").trim(),
    }))
    .filter((slide) => slide.image);
  if (!slides.length) return null;
  return {
    id: String(block.id || createId()),
    type: "slider",
    slides,
  };
}

function normalizeCtaAction(value, fallback = {}) {
  let action = String(value || "").trim();
  if (!action) {
    if (fallback.openPopup) action = "popup";
    else if (fallback.serviceKey) action = "service";
    else if (fallback.href) action = "link";
    else action = "popup";
  }
  if (!["popup", "service", "link"].includes(action)) action = "popup";
  return action;
}

function normalizeCtaBlock(block = {}) {
  const label = String(block.label || "").trim();
  if (!label) return null;
  const action = normalizeCtaAction(block.action, block);
  const serviceKey = String(block.serviceKey || "").trim();
  const href = String(block.href || "").trim();
  if (action === "service" && !serviceKey) return null;
  if (action === "link" && !href) return null;
  return {
    id: String(block.id || createId()),
    type: "cta",
    label,
    action,
    serviceKey,
    href,
  };
}

function normalizeBlock(block = {}) {
  switch (block.type) {
    case "heading":
      return normalizeHeadingBlock(block);
    case "section":
      return normalizeSectionBlock(block);
    case "text":
      return normalizeTextBlock(block);
    case "image":
      return normalizeImageBlock(block);
    case "list":
      return normalizeListBlock(block);
    case "cta":
      return normalizeCtaBlock(block);
    case "slider":
      return normalizeSliderBlock(block);
    default:
      return null;
  }
}

/** @deprecated kept for migrating old article.cta into a block */
function normalizeCta(cta = {}) {
  const label = String(cta.label || "").trim();
  let action = String(cta.action || "").trim();
  if (!action) {
    if (cta.openPopup) action = "popup";
    else if (cta.serviceKey) action = "service";
    else if (cta.href) action = "link";
    else action = "none";
  }
  if (!["none", "popup", "service", "link"].includes(action)) action = "none";
  if (!label) action = "none";
  return {
    label,
    action,
    serviceKey: String(cta.serviceKey || "").trim(),
    href: String(cta.href || "").trim(),
    openPopup: action === "popup",
  };
}

function resolveCtaHref(cta = {}) {
  const action = normalizeCtaAction(cta.action, cta);
  const label = String(cta.label || "").trim();
  if (!label) return "";
  if (action === "popup") return "";
  if (action === "service" && cta.serviceKey) {
    const category = window.NMCategoriesContent?.getCategoryByKey?.(cta.serviceKey)
      || (window.NMCategoriesContent?.getCategories?.() || [])
        .find((item) => item.key === cta.serviceKey || item.slug === cta.serviceKey);
    if (category && typeof window.NMCategoriesContent?.getServicePath === "function") {
      return window.NMCategoriesContent.getServicePath(category);
    }
    const section = window.NMCategoriesContent?.SERVICE_SECTION_SLUG || "izgotovlenie-reklamy";
    return `/uslugi/${section}/${encodeURIComponent(cta.serviceKey)}`;
  }
  if (action === "link") return safeHref(cta.href);
  return "";
}

function normalizeArticle(item = {}, allArticles = []) {
  const title = String(item.title || "").trim();
  if (!title) return null;

  const id = String(item.id || createId());
  const slug = uniqueSlug(
    String(item.slug || "").trim() || slugify(title),
    id,
    allArticles,
  );

  const blocks = (Array.isArray(item.blocks) ? item.blocks : [])
    .map(normalizeBlock)
    .filter(Boolean);

  // Migrate legacy article-level CTA into a content block once.
  const legacyCta = normalizeCta(item.cta || {});
  const hasCtaBlock = blocks.some((block) => block.type === "cta");
  if (!hasCtaBlock && legacyCta.action !== "none" && legacyCta.label) {
    const migrated = normalizeCtaBlock({
      type: "cta",
      label: legacyCta.label,
      action: legacyCta.action,
      serviceKey: legacyCta.serviceKey,
      href: legacyCta.href,
    });
    if (migrated) blocks.push(migrated);
  }

  return {
    id,
    slug,
    title,
    titleHtml: (window.NMSanitize?.sanitizeTitleHtml?.(String(item.titleHtml || "").trim())
      || String(item.titleHtml || "").trim().replace(/<(?!\/?span\b)[^>]*>/gi, ""))
      || buildTitleHtml(title),
    excerpt: String(item.excerpt || "").trim(),
    coverImage: String(item.coverImage || "").trim() || DEFAULT_COVER,
    publishedAt: String(item.publishedAt || "").trim() || todayIso(),
    categoryKeys: Array.isArray(item.categoryKeys)
      ? item.categoryKeys.map((key) => String(key || "").trim()).filter(Boolean)
      : [],
    authorId: String(item.authorId || "").trim(),
    authorName: String(item.authorName || "").trim(),
    authorPost: String(item.authorPost || "").trim(),
    authorImage: String(item.authorImage || "").trim() || DEFAULT_AUTHOR_IMAGE,
    blocks,
  };
}

function getDefaultSeedArticle() {
  return normalizeArticle({
    id: "demo-article-1",
    slug: "chto-u-newmarka-pod-kapotom",
    title: "Что у NEWMARKA под капотом",
    excerpt: "Секреты изготовления рекламы",
    coverImage: DEFAULT_COVER,
    publishedAt: "2024-02-11",
    categoryKeys: ["outdoor", "interior", "transport"],
    authorId: "1",
    authorName: "Юрий Калинин",
    authorPost: "Основатель компании",
    authorImage: DEFAULT_AUTHOR_IMAGE,
    blocks: [
      {
        type: "heading",
        level: 1,
        text: "Заголовок",
        accent: "H1",
      },
      {
        type: "image",
        src: DEFAULT_COVER,
        alt: "",
      },
      {
        type: "heading",
        level: 2,
        text: "Заголовок",
        accent: "H2",
      },
      {
        type: "section",
        text: "Раздел",
        accent: "1",
        anchor: "section-1",
      },
      {
        type: "text",
        variant: "large",
        text: "Текст-заполнитель (strong, b) — это текст, который имеет некоторые характеристики реального письменного текста, но является случайным набором слов или сгенерирован иным образом.",
      },
      {
        type: "text",
        variant: "normal",
        text: "Текст-заполнитель — это текст, который имеет некоторые характеристики реального письменного текста. Его можно использовать для отображения образца шрифтов или [обхода спам-фильтра](#).",
      },
      {
        type: "text",
        variant: "bold",
        text: "Использование вами Сервиса любым способом и в любой форме в пределах его объявленных функциональных возможностей, включая:",
      },
      {
        type: "list",
        ordered: true,
        items: [
          "Оператор ставит своей важнейшей целью соблюдение прав и свобод человека и гражданина при обработке персональных данных.",
          "Защита прав на неприкосновенность частной жизни, личную и семейную тайну.",
        ],
      },
      {
        type: "section",
        text: "Раздел",
        accent: "2",
        anchor: "section-2",
      },
      {
        type: "text",
        variant: "normal",
        text: "Использование вами Сервиса любым способом и в любой форме в пределах его объявленных функциональных возможностей, включая:",
      },
      {
        type: "list",
        ordered: false,
        items: [
          "Оператор ставит своей важнейшей целью соблюдение прав и свобод человека и гражданина при обработке персональных данных.",
          "Защита прав на неприкосновенность частной жизни, личную и семейную тайну.",
        ],
      },
      {
        type: "cta",
        label: "Оставить заявку",
        action: "popup",
      },
      {
        type: "slider",
        slides: [
          { image: DEFAULT_COVER, caption: "Пример работы 1" },
          { image: DEFAULT_COVER, caption: "Пример работы 2" },
          { image: DEFAULT_COVER, caption: "Пример работы 3" },
        ],
      },
    ],
  }, []);
}

function getDefaultData() {
  return {
    articles: [getDefaultSeedArticle()].filter(Boolean),
  };
}

function getArticlesData() {
  const raw = window.NMApi?.cache?.articles;
  if (!raw || !Array.isArray(raw.articles)) {
    return getDefaultData();
  }
  const articles = [];
  raw.articles.forEach((item) => {
    const normalized = normalizeArticle(item, articles);
    if (normalized) articles.push(normalized);
  });
  return { articles };
}

function hasServerArticlesCache() {
  const raw = window.NMApi?.cache?.articles;
  return Boolean(raw && Array.isArray(raw.articles));
}

async function refreshArticlesFromServer() {
  if (!window.NMApi?.request) {
    throw new Error("API недоступен");
  }
  const payload = await window.NMApi.request("/api/public");
  if (payload?.articles != null) {
    window.NMApi.cache.articles = payload.articles;
  }
  return getArticlesData();
}

async function saveArticlesData(data) {
  const articles = [];
  (data?.articles || []).forEach((item) => {
    const normalized = normalizeArticle(item, articles);
    if (normalized) articles.push(normalized);
  });
  const next = { articles };
  if (!window.NMApi?.save) {
    throw new Error("API недоступен");
  }
  try {
    const payload = await window.NMApi.save("articles", next);
    const confirmed = Array.isArray(payload?.articles)
      ? { articles: payload.articles }
      : next;
    const normalizedConfirmed = [];
    (confirmed.articles || []).forEach((item) => {
      const normalized = normalizeArticle(item, normalizedConfirmed);
      if (normalized) normalizedConfirmed.push(normalized);
    });
    const result = { articles: normalizedConfirmed };
    window.NMApi.cache.articles = result;
    document.dispatchEvent(new CustomEvent("nm:articles-updated", { detail: result }));
    return result;
  } catch (error) {
    try {
      await refreshArticlesFromServer();
    } catch (refreshError) {
      // keep whatever cache we still have
    }
    throw error;
  }
}

function getArticles() {
  return getArticlesData().articles;
}

function getArticleById(id) {
  return getArticles().find((item) => item.id === id) || null;
}

function getArticleBySlug(slug) {
  const value = decodeURIComponent(String(slug || "").trim());
  return getArticles().find((item) => item.slug === value) || null;
}

async function upsertArticle(article) {
  let data;
  try {
    data = await refreshArticlesFromServer();
  } catch (error) {
    if (!hasServerArticlesCache()) {
      throw new Error("Нет связи с сервером. Статья не сохранена.");
    }
    data = getArticlesData();
  }

  const next = normalizeArticle(article, data.articles);
  if (!next) return null;
  const index = data.articles.findIndex((item) => item.id === next.id);
  if (index >= 0) data.articles[index] = next;
  else data.articles.unshift(next);
  const saved = await saveArticlesData(data);
  return saved.articles.find((item) => item.id === next.id) || next;
}

async function deleteArticle(id) {
  let data;
  try {
    data = await refreshArticlesFromServer();
  } catch (error) {
    if (!hasServerArticlesCache()) {
      throw new Error("Нет связи с сервером. Статья не удалена.");
    }
    data = getArticlesData();
  }
  data.articles = data.articles.filter((item) => item.id !== id);
  await saveArticlesData(data);
}

function getSlugFromLocation() {
  try {
    const match = window.location.pathname.match(/^\/article\/([^/]+)\/?$/i);
    if (match?.[1]) return decodeURIComponent(match[1]);
    const params = new URLSearchParams(window.location.search);
    return params.get("slug") || params.get("article") || "";
  } catch (error) {
    return "";
  }
}

function resolveArticleAuthor(article) {
  const authorId = String(article?.authorId || "").trim();
  const authorName = String(article?.authorName || "").trim();
  const about = window.NMAboutContent;
  const data = about?.getAboutData?.();
  const keys = about?.TEAM_KEYS || ["leaders", "marketing", "designers", "production"];

  let person = null;
  if (authorId && about?.findPersonById && data) {
    person = about.findPersonById(data, authorId);
  }
  if (!person && authorName && data) {
    person = keys
      .flatMap((key) => data.teams?.[key] || [])
      .find((item) => String(item.name || "").trim().toLowerCase() === authorName.toLowerCase())
      || null;
  }

  if (person) {
    return {
      authorId: person.id,
      authorName: person.name || authorName,
      authorPost: person.post || String(article?.authorPost || "").trim(),
      authorImage: person.image || String(article?.authorImage || "").trim() || DEFAULT_AUTHOR_IMAGE,
    };
  }

  return {
    authorId,
    authorName,
    authorPost: String(article?.authorPost || "").trim(),
    authorImage: String(article?.authorImage || "").trim() || DEFAULT_AUTHOR_IMAGE,
  };
}

function renderHeadingHtml(block) {
  const tag = `h${block.level}`;
  const accent = block.accent ? ` <span>${escapeHtml(block.accent)}</span>` : "";
  return `<${tag}>${escapeHtml(block.text)}${accent}</${tag}>`;
}

function renderSectionHtml(block) {
  const accent = block.accent ? ` <span>${escapeHtml(block.accent)}</span>` : "";
  return `<h2 id="${escapeHtml(block.anchor)}">${escapeHtml(block.text)}${accent}</h2>`;
}

function renderTextHtml(block) {
  const html = renderInlineMarkdown(block.text);
  if (block.variant === "large") return `<strong>${html}</strong>`;
  if (block.variant === "bold") return `<b>${html}</b>`;
  return `<p>${html}</p>`;
}

function renderImageHtml(block) {
  return `<p><img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}"></p>`;
}

function renderListHtml(block) {
  const tag = block.ordered ? "ol" : "ul";
  const items = block.items.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join("");
  return `<${tag}>${items}</${tag}>`;
}

function renderContentBlockHtml(block) {
  switch (block.type) {
    case "heading":
      return renderHeadingHtml(block);
    case "section":
      return renderSectionHtml(block);
    case "text":
      return renderTextHtml(block);
    case "image":
      return renderImageHtml(block);
    case "list":
      return renderListHtml(block);
    default:
      return "";
  }
}

function renderSliderHtml(block) {
  if (!block?.slides?.length) return "";
  const slides = block.slides.map((slide) => `
    <div class="big-slider__slide swiper-slide" data-text="${escapeHtml(slide.caption || "")}">
      <div class="big-slider__slide-img" data-swiper-parallax="60%"
        style="background-image: url('${escapeHtml(slide.image)}')">
      </div>
    </div>
  `).join("");

  return `
    <div class="big-slider">
      <div class="big-slider__swiper-container">
        <div class="big-slider__swiper swiper">
          <div class="big-slider__swiper-wrapper swiper-wrapper">
            ${slides}
          </div>
          <div class="big-slider__button-prev"></div>
          <div class="big-slider__button-next"></div>
        </div>
        <div class="swiper-pagination"></div>
        <div class="slider-pagination-count big-slider__pagination-count">
          <span class="big-slider__pagination-current h5">01</span>
          <span class="big-slider__pagination-line"></span>
          <span class="big-slider__pagination-total d2"></span>
        </div>
      </div>
    </div>
  `;
}

function renderCtaHtml(block) {
  const href = resolveCtaHref(block);
  const label = escapeHtml(block.label);
  if (block.action === "popup") {
    return `<button class="blog__btn btn btn_l popup-open-js" type="button" data-article-cta data-popup="popup-feedback">${label}</button>`;
  }
  if (!href) return "";
  return `<button class="blog__btn btn btn_l" type="button" data-article-cta data-article-cta-href="${escapeHtml(href)}">${label}</button>`;
}

function renderArticleBodyHtml(blocks) {
  let html = "";
  let contentBuffer = "";

  const flushContent = () => {
    if (!contentBuffer) return;
    html += `<div class="user-content">${contentBuffer}</div>`;
    contentBuffer = "";
  };

  (blocks || []).forEach((block) => {
    if (block.type === "cta") {
      flushContent();
      html += renderCtaHtml(block);
      return;
    }
    if (block.type === "slider") {
      flushContent();
      html += `<div class="blog__big-slider">${renderSliderHtml(block)}</div>`;
      return;
    }
    contentBuffer += `${renderContentBlockHtml(block)}\n`;
  });

  flushContent();
  return html;
}

function renderBlocksToContentHtml(blocks) {
  return (blocks || [])
    .filter((block) => block.type !== "slider" && block.type !== "cta")
    .map(renderContentBlockHtml)
    .join("\n");
}

function bindArticleCtaButtons(root) {
  root?.querySelectorAll("[data-article-cta]").forEach((button) => {
    const href = button.getAttribute("data-article-cta-href");
    button.addEventListener("click", (event) => {
      event.preventDefault();
      if (href) {
        window.location.href = href;
        return;
      }
      if (typeof window.openPopup === "function") {
        window.openPopup(button.getAttribute("data-popup") || "popup-feedback");
      }
    });
  });
}

function getSectionBlocks(blocks) {
  return (blocks || []).filter((block) => block.type === "section");
}

function getCategoryTitles(categoryKeys) {
  const categories = window.NMCategoriesContent?.getCategories?.() || [];
  return (categoryKeys || [])
    .map((key) => categories.find((item) => item.key === key || item.slug === key || item.id === key))
    .filter(Boolean);
}

function initArticleBigSlider(root) {
  const swiperEl = root?.querySelector(".big-slider__swiper");
  if (!swiperEl || swiperEl.swiper) return;

  try {
    // eslint-disable-next-line global-require
    const Swiper = require("swiper").default;
    // eslint-disable-next-line global-require
    const { Navigation, Pagination, Parallax } = require("swiper/modules");
    // eslint-disable-next-line global-require
    const { gsap } = require("gsap");

    const instance = new Swiper(swiperEl, {
      modules: [Navigation, Pagination, Parallax],
      speed: 1000,
      parallax: true,
      navigation: {
        nextEl: root.querySelector(".big-slider__button-next"),
        prevEl: root.querySelector(".big-slider__button-prev"),
      },
      pagination: {
        el: root.querySelector(".big-slider__pagination-count .big-slider__pagination-total"),
        type: "custom",
        renderCustom(_swiper, _current, total) {
          return total >= 10 ? String(total) : `0${total}`;
        },
      },
    });

    const current = root.querySelector(".big-slider__pagination-current");
    instance.on("slideChange", () => {
      const inx = instance.realIndex + 1;
      if (!current) return;
      gsap.to(current, {
        duration: 0.2,
        y: -20,
        opacity: 0,
        ease: "power1.in",
        onComplete: () => {
          current.innerHTML = inx >= 10 ? String(inx) : `0${inx}`;
          gsap.fromTo(current, { y: 20, opacity: 0 }, {
            duration: 0.2,
            y: 0,
            opacity: 1,
            ease: "power1.out",
          });
        },
      });
    });
  } catch (error) {
    // Swiper may already be initialized by the global bundle.
  }
}

function hydrateArticleDetails() {
  const root = document.querySelector("[data-article-root]");
  if (!root) return;

  const slug = getSlugFromLocation();
  const article = slug ? getArticleBySlug(slug) : null;
  if (!article) {
    if (slug) window.location.replace("/error-page");
    return;
  }

  const titleNode = root.querySelector("[data-article-title]");
  if (titleNode) titleNode.textContent = article.title;

  document.title = `Новая марка | ${article.title}`;

  const crumb = document.querySelector(".news-details-page .breadcrumbs__item:last-child [itemprop=\"name\"]");
  if (crumb) crumb.textContent = article.title;

  const tagsRoot = root.querySelector("[data-article-tags]");
  if (tagsRoot) {
    const cats = getCategoryTitles(article.categoryKeys);
    tagsRoot.innerHTML = cats.length
      ? cats.map((cat) => (
        `<a class="tags__link btn-s" href="/articles?category=${encodeURIComponent(cat.key)}">${escapeHtml(cat.title)}</a>`
      )).join("")
      : "";
  }

  const author = resolveArticleAuthor(article);
  const authorImg = root.querySelector("[data-article-author-img]");
  if (authorImg) {
    authorImg.src = author.authorImage || DEFAULT_AUTHOR_IMAGE;
    authorImg.alt = author.authorName || "";
  }
  const authorDate = root.querySelector("[data-article-author-date]");
  if (authorDate) {
    const dateLabel = formatDate(article.publishedAt) || formatDate(todayIso());
    authorDate.textContent = `${dateLabel}, под редакцией`;
  }
  const authorName = root.querySelector("[data-article-author-name]");
  if (authorName) authorName.textContent = author.authorName || "Автор";
  const authorPost = root.querySelector("[data-article-author-post]");
  if (authorPost) authorPost.textContent = author.authorPost || "";

  const tocRoot = root.querySelector("[data-article-toc]");
  if (tocRoot) {
    const sections = getSectionBlocks(article.blocks);
    tocRoot.innerHTML = sections.map((section) => (
      `<a class="blog__link-contents anchor-link d2" href="#${escapeHtml(section.anchor)}">${escapeHtml(section.text)}${section.accent ? ` ${escapeHtml(section.accent)}` : ""}</a>`
    )).join("");
    tocRoot.hidden = !sections.length;
  }

  const bodyRoot = root.querySelector("[data-article-body]");
  if (bodyRoot) {
    bodyRoot.innerHTML = renderArticleBodyHtml(article.blocks);
    bindArticleCtaButtons(bodyRoot);
    bodyRoot.querySelectorAll(".blog__big-slider").forEach((sliderHost) => {
      initArticleBigSlider(sliderHost);
    });
  }

  if (typeof window.NMPortfolioContent?.renderArticlesPortfolio === "function") {
    window.NMPortfolioContent.renderArticlesPortfolio(article.categoryKeys);
  }
}

function renderArticlesList() {
  const list = document.querySelector("[data-articles-list]");
  if (!list) return;

  const params = new URLSearchParams(window.location.search);
  const filterKey = String(params.get("category") || "").trim();
  let articles = getArticles();
  if (filterKey) {
    articles = articles.filter((item) => item.categoryKeys.includes(filterKey));
  }

  const tagsRoot = document.querySelector("[data-articles-tags]");
  if (tagsRoot) {
    const usedKeys = new Set();
    getArticles().forEach((article) => {
      article.categoryKeys.forEach((key) => usedKeys.add(key));
    });
    const cats = getCategoryTitles([...usedKeys]);
    tagsRoot.innerHTML = `
      <div class="tags">
        <div class="tags__items">
          <a class="tags__link btn-s${filterKey ? "" : " is-active"}" href="/articles">Все</a>
          ${cats.map((cat) => `
            <a class="tags__link btn-s${filterKey === cat.key ? " is-active" : ""}" href="/articles?category=${encodeURIComponent(cat.key)}">${escapeHtml(cat.title)}</a>
          `).join("")}
        </div>
      </div>
    `;
  }

  if (!articles.length) {
    list.innerHTML = `
      <li class="articles__item">
        <div class="info-preview">
          <div class="info-preview__content">
            <h5 class="info-preview__title h5">Пока нет статей</h5>
            <p class="info-preview__desc d2">Добавьте статьи в разделе «Контент → Статьи»</p>
          </div>
        </div>
      </li>
    `;
    return;
  }

  list.innerHTML = articles.map((article) => {
    const cats = getCategoryTitles(article.categoryKeys).slice(0, 3);
    return `
      <li class="articles__item">
        <a class="info-preview" href="${escapeHtml(getArticlePath(article))}">
          <div class="info-preview__img">
            <img src="${escapeHtml(article.coverImage || DEFAULT_COVER)}" alt="${escapeHtml(article.title)}">
          </div>
          <div class="info-preview__content">
            <div class="info-preview__tags">
              ${cats.map((cat) => `<span>${escapeHtml(cat.title)}</span>`).join("")}
            </div>
            <h5 class="info-preview__title h5">${escapeHtml(article.title)}</h5>
            <p class="info-preview__desc d2">${escapeHtml(article.excerpt || "")}</p>
            <span class="info-preview__btn btn btn_arrow">
              <svg width="32" height="32" viewBox="0 0 32 32">
                <use xlink:href="#icon-arrow-right"></use>
              </svg>
            </span>
          </div>
        </a>
      </li>
    `;
  }).join("");
}

function renderHomeArticleCard(article) {
  const cats = getCategoryTitles(article.categoryKeys).slice(0, 3);
  return `
    <a class="info-preview" href="${escapeHtml(getArticlePath(article))}">
      <div class="info-preview__img">
        <img src="${escapeHtml(article.coverImage || DEFAULT_COVER)}" alt="${escapeHtml(article.title)}">
      </div>
      <div class="info-preview__content">
        <div class="info-preview__tags">
          ${cats.map((cat) => `<span>${escapeHtml(cat.title)}</span>`).join("")}
        </div>
        <h5 class="info-preview__title h5">${escapeHtml(article.title)}</h5>
        <p class="info-preview__desc d2">${escapeHtml(article.excerpt || "")}</p>
        <span class="info-preview__btn btn btn_arrow">
          <svg width="32" height="32" viewBox="0 0 32 32">
            <use xlink:href="#icon-arrow-right"></use>
          </svg>
        </span>
      </div>
    </a>
  `;
}

function hydrateHomeArticles() {
  const wrapper = document.querySelector("[data-home-articles]");
  const section = wrapper?.closest(".articles-section");
  if (!wrapper) return;

  const articles = [...getArticles()].sort((a, b) => {
    const left = String(b.publishedAt || "");
    const right = String(a.publishedAt || "");
    return left.localeCompare(right);
  });

  if (!articles.length) {
    if (section) section.hidden = true;
    wrapper.innerHTML = "";
    return;
  }

  if (section) section.hidden = false;
  wrapper.innerHTML = articles.map((article) => (
    `<div class="slide-row__slide swiper-slide">${renderHomeArticleCard(article)}</div>`
  )).join("");

  const swiperRoot = wrapper.closest(".swiper");
  if (swiperRoot?.swiper) {
    swiperRoot.swiper.update();
    swiperRoot.swiper.slideTo(0, 0);
  }
}

function hydrateArticles() {
  hydrateHomeArticles();
  if (document.querySelector(".articles-page")) {
    renderArticlesList();
  }
  if (document.querySelector("[data-article-root]")) {
    hydrateArticleDetails();
  }
}

window.NMArticlesContent = {
  STORAGE_KEY,
  DEFAULT_COVER,
  DEFAULT_AUTHOR_IMAGE,
  BLOCK_TYPES,
  createId,
  escapeHtml,
  buildTitleHtml,
  slugify,
  uniqueSlug,
  getArticlePath,
  formatDate,
  todayIso,
  normalizeBlock,
  normalizeArticle,
  normalizeCta,
  normalizeCtaBlock,
  resolveCtaHref,
  renderArticleBodyHtml,
  getArticlesData,
  saveArticlesData,
  refreshArticlesFromServer,
  getArticles,
  getArticleById,
  getArticleBySlug,
  upsertArticle,
  deleteArticle,
  renderBlocksToContentHtml,
  renderSliderHtml,
  getSectionBlocks,
  getCategoryTitles,
  hydrateArticles,
  hydrateArticleDetails,
  renderArticlesList,
};

window.NMApi?.ready?.then(hydrateArticles);
